import { Link } from "react-router-dom";
import MenuSheet from "@/components/MenuSheet";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, FileText, Mail } from "lucide-react";

export default function TermsPage() {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const updated = "June 10, 2026";
  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-3">
          {isAuthed && <MenuSheet />}
          <Button variant="ghost" size="sm" asChild className="rounded-xl" data-testid="terms-back-button">
            <Link to={isAuthed ? "/" : "/login"}>
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              {isAuthed ? "Back to planner" : "Back to sign in"}
            </Link>
          </Button>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <FileText strokeWidth={1.5} className="size-3.5" />
            Terms
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Terms of service
          </h1>
          <p className="text-sm text-muted-foreground">Last updated: {updated}</p>
        </div>

        <Card className="rounded-2xl">
          <CardContent className="pt-6 space-y-6 text-[15px] leading-relaxed">
            <section className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4">
              <p className="m-0">
                Welcome to Lumivera. By creating an account or using the app, you agree to these terms. Please read them — they're written in plain English to be as clear as possible.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">1. Who we are</h2>
              <p className="m-0">
                Lumivera ("we", "us", "the app") is a personal life-planning web application that lets you track events, tasks, goals, reflections, letters to your future self, and vision boards. It is operated by the individual reachable at{" "}
                <a
                  href="mailto:amendozalp0220@gmail.com"
                  className="font-semibold text-primary underline underline-offset-4"
                >
                  amendozalp0220@gmail.com
                </a>{" "}
                ("we").
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">2. Your account</h2>
              <ul className="list-disc pl-6 space-y-1 m-0">
                <li>You must be at least 13 years old to use Lumivera.</li>
                <li>You're responsible for keeping your password secure. Don't share it.</li>
                <li>You're responsible for all activity that happens under your account.</li>
                <li>If you suspect unauthorized access, change your password and contact us.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">3. Your content stays yours</h2>
              <p className="m-0">
                Everything you put into Lumivera — your goals, journal entries, letters, vision board images, reviews — remains <strong>your property</strong>. By using the service, you grant us a limited, non-exclusive license to store and display that content back to you (and only you, except for reviews you submit publicly to us).
              </p>
              <p className="m-0">We do not own your content. We don't claim it. We don't sell it.</p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">4. Acceptable use</h2>
              <p className="m-0">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1 m-0">
                <li>Use Lumivera to break any law.</li>
                <li>Upload content you don't have rights to (e.g. copyrighted images you don't own a license to).</li>
                <li>Try to hack, scrape, reverse-engineer, or disrupt the app or its servers.</li>
                <li>Use the app to harass, harm, or impersonate anyone.</li>
                <li>Upload viruses, malware, or other harmful code.</li>
              </ul>
              <p className="m-0">
                We may suspend or close accounts that violate these rules.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">5. Service availability</h2>
              <p className="m-0">
                We do our best to keep Lumivera running smoothly, but we offer the service{" "}
                <strong>"as is"</strong> without warranties of any kind. We can't guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-1 m-0">
                <li>That the app will be available without interruption.</li>
                <li>That the app will be free of bugs, errors, or downtime.</li>
                <li>That data will never be lost — please keep backups of anything irreplaceable.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">6. Limitation of liability</h2>
              <p className="m-0">
                To the maximum extent allowed by law, Lumivera and its operator are not liable for any indirect, incidental, consequential, or punitive damages arising from your use of the service — including but not limited to lost data, lost productivity, or emotional distress.
              </p>
              <p className="m-0">
                If a court finds we are liable for something, our total liability is limited to the amount you have paid us in the past 12 months (which for the free service is $0).
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">7. Not medical or professional advice</h2>
              <p className="m-0">
                Lumivera is a planning and reflection tool. Nothing in the app — including quotes, prompts, or features like the personal log — constitutes medical, psychological, legal, financial, or professional advice. If you're struggling, please reach out to a qualified professional.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">8. Changes to the service or terms</h2>
              <p className="m-0">
                We may add, change, or remove features at any time. We may also update these terms. When we do, we'll update the "Last updated" date above. Continued use of the app after changes means you accept the updated terms.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">9. Termination</h2>
              <p className="m-0">
                You can delete your account at any time by emailing us. We can suspend or terminate your account if you violate these terms.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0">10. Governing law</h2>
              <p className="m-0">
                These terms are governed by the laws of the United States. Any disputes will be handled in courts located in the operator's jurisdiction unless otherwise required by applicable consumer protection laws.
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="font-display text-xl font-bold m-0 flex items-center gap-2">
                <Mail strokeWidth={1.5} className="size-5 text-primary" />
                11. Contact
              </h2>
              <p className="m-0">
                Questions about these terms? Email{" "}
                <a
                  href="mailto:amendozalp0220@gmail.com"
                  className="font-semibold text-primary underline underline-offset-4"
                >
                  amendozalp0220@gmail.com
                </a>.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
